import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import { createPortal } from 'react-dom';
import styles from './NoteModal.module.css';
import aiNotesStyles from '../ChapterAINotes/ChapterAINotes.module.css';
import { CloseIcon, HeartIcon, LoaderIcon } from '../../../../components/Icons';
import { API_BASE_URL } from '../../../../services/api';
import api from '../../../../services/api';

const AI_TABS = [
  {
    key: 'sentenceAnalysis',
    label: '句子解析',
    prompt: '请像英文老师讲解句子一样，针对上述英文内容进行解析：1）列出关键短语（动词短语、固定搭配、介词短语等），每条使用"短语 — 中文解释 — 在句子中的作用 — 常用例句"的格式，例句需与原句不同；2）若出现特别特俗的固定搭配（如 not only ... but also ...），额外说明其使用规则。不需要分析主语/谓语/状语等语法成分，也不需要学习建议；无法解析时请输出空。'
  },
  {
    key: 'keywords',
    label: '重点单词',
    prompt: '仅输出重点单词列表，每行严格使用 - 单词 中文解释 + 根据单词新造的例句 的格式。禁止输出任何分析、思考、说明、总结、前言或后语；如无法遵守请直接输出空。'
  },
  {
    key: 'phrases',
    label: '短语列表',
    prompt: '仅输出关键短语列表，每行严格使用 - 短语 中文解释 + 根据短语举一反三的例句 的格式。禁止输出任何分析或多余文字；若无内容请输出空。'
  },
  {
    key: 'grammar',
    label: '语法分析',
    prompt: '仅输出语法要点列表，每行严格使用 - 语法点：简要说明 的格式。禁止输出分析过程或额外文字；若无内容请输出空。'
  },
  {
    key: 'altTranslation',
    label: '举一反三',
    useOriginalText: true,
    prompt: '请将上述中文句子翻译为3种不同风格的英文，并简要说明每种翻译的特点和用到的关键表达：\n1）**直译版**：忠实原文，逐句对应\n2）**意译版**：自然流畅，符合英文表达习惯\n3）**地道版**：使用英文习语或高级表达\n\n每种翻译格式如下：\n- **直译**：英文翻译\n  亮点：用到的关键词汇或句型\n- **意译**：英文翻译\n  亮点：用到的关键词汇或句型\n- **地道**：英文翻译\n  亮点：用到的关键词汇或句型'
  }
];

function DbAiNoteCard({ note, displayId, onWordClick, aiNotesStyles, onSaveAiNote, isSaved, isSaving, savedNoteId }) {
  const englishText = (note.sentence_en ?? '').toString().trim();
  const chineseText = (note.sentence_zh ?? '').toString().trim();
  const keyPhrases = Array.isArray(note.key_phrases) ? note.key_phrases : [];

  const renderEnglishText = (text, keyPrefix) => {
    if (!text || typeof text !== 'string') return text;
    const invokeWordClick = typeof onWordClick === 'function' ? onWordClick : null;
    const parts = text.split(/(\b[A-Za-z][A-Za-z''-]*\b)/);
    return parts.map((part, index) => {
      if (part && /^[A-Za-z][A-Za-z''-]*$/.test(part)) {
        if (!invokeWordClick) return <Fragment key={`${keyPrefix}-${index}`}>{part}</Fragment>;
        return (
          <span
            key={`${keyPrefix}-${index}`}
            className={aiNotesStyles.clickableWord}
            role="button"
            tabIndex={0}
            onClick={() => invokeWordClick(part, displayId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                invokeWordClick(part, displayId);
              }
            }}
          >
            {part}
          </span>
        );
      }
      return <Fragment key={`${keyPrefix}-${index}`}>{part}</Fragment>;
    });
  };

  return (
    <div className={aiNotesStyles.aiNoteItem}>
      <div className={aiNotesStyles.aiNoteSentenceHeader}>
        <div className={aiNotesStyles.aiNoteHeaderLeft}>
          <span className={aiNotesStyles.aiNoteSentenceIndex}>#{displayId}</span>
        </div>
        {typeof onSaveAiNote === 'function' && (
          <button
            type="button"
            className={`${aiNotesStyles.favoriteButton} ${isSaved ? aiNotesStyles.favoriteButtonActive : ''}`}
            onClick={() => onSaveAiNote(displayId, { noteId: savedNoteId ?? null })}
            disabled={isSaving}
            title={isSaved ? '移除 AI 笔记' : '收藏 AI 笔记'}
          >
            {isSaving ? (
              <LoaderIcon size={16} />
            ) : (
              <>
                <HeartIcon size={16} filled={isSaved} />
                <span className={aiNotesStyles.favoriteButtonText}>
                  {isSaved ? '已收藏' : '收藏'}
                </span>
              </>
            )}
          </button>
        )}
      </div>
      {(englishText || chineseText) && (
        <div className={aiNotesStyles.aiNoteSentences}>
          {englishText && (
            <div className={aiNotesStyles.aiNoteSentenceEn}>
              {renderEnglishText(englishText, `db-sentence-en`)}
            </div>
          )}
          {chineseText && (
            <div className={aiNotesStyles.aiNoteSentenceZh}>{chineseText}</div>
          )}
        </div>
      )}
      {keyPhrases.length > 0 && (
        <div className={aiNotesStyles.aiNotePhrases}>
          <h4 className={aiNotesStyles.aiNotePhrasesTitle}>
            <span className="material-icons-round" style={{ fontSize: '14px' }}>auto_awesome</span>
            重点短语
          </h4>
          {keyPhrases.map((phrase, phraseIdx) => (
            <div key={`phrase-${phraseIdx}`} className={aiNotesStyles.aiNotePhraseItem}>
              <div className={aiNotesStyles.aiNotePhraseHeader}>
                <span className={aiNotesStyles.aiNotePhraseTitle}>
                  {phrase.phrase
                    ? renderEnglishText(phrase.phrase, `phrase-${phraseIdx}`)
                    : '短语'}
                </span>
                {phrase.meaning && (
                  <span className={aiNotesStyles.aiNotePhraseMeaning}>{phrase.meaning}</span>
                )}
              </div>
              {phrase.usage_note && (
                <p className={aiNotesStyles.aiNotePhraseUsage}>
                  <span className={aiNotesStyles.aiNoteMetaLabel}>用法</span>
                  {phrase.usage_note}
                </p>
              )}
              {(phrase.example_en || phrase.example_cn) && (
                <div className={aiNotesStyles.aiNotePhraseExampleBox}>
                  {phrase.example_en && (
                    <p className={aiNotesStyles.aiNotePhraseExample}>
                      {renderEnglishText(phrase.example_en, `ex-${phraseIdx}`)}
                    </p>
                  )}
                  {phrase.example_cn && (
                    <p className={aiNotesStyles.aiNotePhraseExampleCn}>{phrase.example_cn}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const normalizeKeyPhrases = (keyPhrasesSource) => {
  const arr = Array.isArray(keyPhrasesSource) ? keyPhrasesSource : [];
  return arr
    .map((phrase) => {
      if (!phrase || typeof phrase !== 'object') return null;
      const phraseText = phrase.phrase ?? phrase.text ?? '';
      const meaning = phrase.meaning ?? phrase.translation ?? '';
      const usageNote = phrase.usage_note ?? phrase.usageNote ?? phrase.note ?? '';
      const exampleEn = phrase.example_en ?? phrase.exampleEn ?? phrase.example ?? '';
      const exampleCn = phrase.example_cn ?? phrase.exampleCn ?? phrase.example_translation ?? phrase.exampleTranslation ?? '';
      if (!phraseText && !meaning && !usageNote && !exampleEn && !exampleCn) return null;
      return { phrase: phraseText, meaning, usage_note: usageNote, example_en: exampleEn, example_cn: exampleCn };
    })
    .filter(Boolean);
};

const NoteModal = ({
  visible,
  sentenceIndex = null,
  originalText = '',
  translatedText = '',
  value,
  loading = false,
  error = '',
  onChange,
  onClose,
  onSubmit,
  novelId,
  chapterId,
  volumeId = null,
  chapterAiNotes = null,
  onWordClick = null,
  onSaveAiNote = null,
  aiNoteSaved = {},
  aiNoteSavedSet,
  aiNoteSaving = {},
}) => {
  const tabConfigs = useMemo(() => AI_TABS, []);

  const dbAiNoteForSentence = useMemo(() => {
    if (chapterAiNotes == null || (typeof chapterAiNotes === 'string' && !chapterAiNotes.trim())) return null;
    let parsed;
    try {
      if (Array.isArray(chapterAiNotes)) parsed = chapterAiNotes;
      else if (typeof chapterAiNotes === 'string') parsed = JSON.parse(chapterAiNotes);
      else if (typeof chapterAiNotes === 'object') parsed = Array.isArray(chapterAiNotes.notes) ? chapterAiNotes.notes : chapterAiNotes;
      else parsed = [];
    } catch {
      return null;
    }
    const list = Array.isArray(parsed) ? parsed : [];
    const sentenceId = sentenceIndex != null ? Number(sentenceIndex) : null;
    const match = list.find((item) => {
      if (!item || typeof item !== 'object') return false;
      const id = item.sentence_id ?? item.sentenceId ?? item.id ?? item.index;
      if (id == null) return false;
      const num = Number(id);
      return num === sentenceId || id === sentenceId || num === sentenceId + 1 || id === String(sentenceId);
    });
    if (!match) return null;
    const sentenceIdRes = match.sentence_id ?? match.sentenceId ?? sentenceId;
    const keyPhrases = normalizeKeyPhrases(match.key_phrases ?? match.keyPhrases);
    return {
      sentence_id: sentenceIdRes,
      sentence_en: match.sentence_en ?? match.sentenceEn ?? translatedText,
      sentence_zh: match.sentence_zh ?? match.sentenceZh ?? originalText,
      key_phrases: keyPhrases,
    };
  }, [chapterAiNotes, sentenceIndex, originalText, translatedText]);

  const createEmptyResponses = useCallback(() => {
    const empty = {};
    tabConfigs.forEach((tab) => {
      empty[tab.key] = '';
    });
    return empty;
  }, [tabConfigs]);

  const [activeTab, setActiveTab] = useState('dbAi');
  const [activeAiTab, setActiveAiTab] = useState(tabConfigs[0].key);
  const [aiResponses, setAiResponses] = useState(createEmptyResponses);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiStreamingTab, setAiStreamingTab] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiSaveStatus, setAiSaveStatus] = useState({});
  const [aiManualStopFlags, setAiManualStopFlags] = useState({});

  const controllerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setActiveTab('dbAi');
      setActiveAiTab(tabConfigs[0].key);
      setAiResponses(createEmptyResponses);
      setAiError('');
      setAiSaveStatus({});
      setAiStreaming(false);
      setAiStreamingTab(null);
      setAiManualStopFlags({});
    } else {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      setAiStreaming(false);
      setAiStreamingTab(null);
      setAiManualStopFlags({});
    }
  }, [visible, sentenceIndex, originalText, translatedText, tabConfigs, createEmptyResponses]);

  const formatWithBold = useCallback((text) => {
    if (!text) return text;
    return text
      .split(/(\*\*[\s\S]*?\*\*)/g)
      .filter(Boolean)
      .map((segment, idx) => {
        if (segment.startsWith('**') && segment.endsWith('**')) {
          const inner = segment.slice(2, -2);
          return (
            <strong key={`bold-${idx}`} className={styles.aiEmphasis}>
              {inner}
            </strong>
          );
        }
        return <React.Fragment key={`text-${idx}`}>{segment}</React.Fragment>;
      });
  }, []);

  const stopStreaming = useCallback((manual = true) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (manual && aiStreamingTab) {
      setAiManualStopFlags((prev) => ({ ...prev, [aiStreamingTab]: true }));
    }
    setAiStreaming(false);
    setAiStreamingTab(null);
  }, [aiStreamingTab]);

  const fetchAiContent = useCallback(async (tabKey, force = false) => {
    if (!translatedText && !force) {
      // 没有英文上下文时仍允许触发，但提示用户
    }

    setAiError('');

    if (!force && aiResponses[tabKey]) {
      return;
    }

    stopStreaming(false);

    setAiManualStopFlags((prev) => ({ ...prev, [tabKey]: false }));

    const controller = new AbortController();
    controllerRef.current = controller;

    setAiResponses((prev) => ({ ...prev, [tabKey]: '' }));
    setAiStreaming(true);
    setAiStreamingTab(tabKey);

    const tabConfig = tabConfigs.find((tab) => tab.key === tabKey) || tabConfigs[0];

    let systemPrompt;
    if (tabConfig.useOriginalText) {
      // 举一反三：用中文原文作为上下文
      const contextLines = [];
      if (originalText) {
        contextLines.push(`中文原文：${originalText.trim().slice(0, 800)}`);
      }
      if (translatedText) {
        contextLines.push(`当前英文翻译（供参考）：${translatedText.trim().slice(0, 800)}`);
      }
      systemPrompt = `你是一名资深英文翻译老师，请根据以下中文句子进行多角度英文翻译教学。\n${contextLines.join('\n')}\n不需要分析故事背景，直接给出翻译和亮点说明，用中文作答，控制在300字内。`;
    } else {
      // 其他Tab：用英文译文作为上下文
      const contextLines = [];
      if (translatedText) {
        contextLines.push(`英文内容：${translatedText.trim().slice(0, 800)}`);
      }
      systemPrompt = `你是一名英文老师，请根据以下英文句子提取英文知识点，\n${contextLines.join('\n')}不需要分析故事背景，直接给出列点／条目符号形式中文作答，不需要给出思考过程，控制在200字内。`;
    }

    const requestMessages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: tabConfig.prompt }
    ];

    try {
      const response = await fetch(`${API_BASE_URL}/api/guiji-llm/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messages: requestMessages }),
        signal: controller.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || '调用大模型接口失败');
      }

      if (!response.body) {
        throw new Error('浏览器不支持流式响应');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let doneStreaming = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const segments = buffer.split('\n\n');
        buffer = segments.pop() ?? '';

        for (const segment of segments) {
          const trimmedSegment = segment.trim();
          if (!trimmedSegment) continue;

          const lines = trimmedSegment.split('\n');

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line || !line.startsWith('data:')) continue;

            const content = line.slice(5).trim();
            if (!content) continue;

            if (content === '[DONE]') {
              doneStreaming = true;
              break;
            }

            try {
              const parsed = JSON.parse(content);
              const delta = parsed?.choices?.[0]?.delta?.content || '';
              if (delta) {
                setAiResponses((prev) => ({
                  ...prev,
                  [tabKey]: `${prev[tabKey] || ''}${delta}`
                }));
              }
            } catch (err) {
              console.error('解析流式响应失败:', err, content);
            }
          }

          if (doneStreaming) break;
        }

        if (doneStreaming) break;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('AI请求失败:', error);
      setAiError(typeof error.message === 'string' ? error.message : 'AI请求失败，请稍后重试');
    } finally {
      setAiStreaming(false);
      setAiStreamingTab(null);
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    }
  }, [tabConfigs, translatedText, originalText, aiResponses, stopStreaming]);

  useEffect(() => {
    if (!visible) return;
    if (activeTab !== 'realtimeAi') return;
    if (aiStreaming) return;
    if (aiManualStopFlags[activeAiTab]) return;
    if (aiResponses[activeAiTab]) return;
    fetchAiContent(activeAiTab);
  }, [visible, activeTab, activeAiTab, aiStreaming, aiResponses, aiManualStopFlags, fetchAiContent]);

  const handleAiTabSelect = (tabKey) => {
    if (activeAiTab === tabKey) {
      if (!aiResponses[tabKey] && !aiStreaming) {
        fetchAiContent(tabKey, true);
      }
      return;
    }
    setActiveAiTab(tabKey);
    if (activeTab === 'realtimeAi' && !aiResponses[tabKey]) {
      fetchAiContent(tabKey);
    }
  };

  const handleSaveAiResponse = async (tabKey) => {
    const content = aiResponses[tabKey];
    if (!content || !content.trim()) {
      return;
    }

    if (!chapterId || !novelId) {
      setAiError('缺少章节或小说信息，无法保存AI笔记');
      return;
    }

    setAiSaveStatus((prev) => ({ ...prev, [tabKey]: 'loading' }));

    try {
      await api.post('/user-notes/', {
        note_content: content.trim(),
        sentence_id: typeof sentenceIndex === 'number' ? sentenceIndex : 0,
        chapter_id: chapterId,
        novel_id: novelId,
        volume_id: volumeId,
      });

      setAiSaveStatus((prev) => ({ ...prev, [tabKey]: 'success' }));
    } catch (err) {
      console.error('保存AI回复失败:', err);
      setAiSaveStatus((prev) => ({ ...prev, [tabKey]: 'error' }));

      if (err.response?.status === 401) {
        alert('请先登录后再收藏AI回复');
      } else if (err.response?.status === 403) {
        // 403 由 api 拦截器处理（跳转 VipLimitPage），此处不弹窗
        console.log('每日限额已达上限');
      } else {
        setAiError('保存AI回复失败，请稍后重试');
      }
    }
  };

  if (!visible) {
    return null;
  }

  const currentAiResponse = aiResponses[activeAiTab] || '';
  const currentAiSaveStatus = aiSaveStatus[activeAiTab];

  const modalContent = (
    <div
      className={styles.overlay}
      onClick={() => {
        if (!loading) {
          onClose?.();
        }
      }}
      role="presentation"
    >
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          <h3 className={styles.title}>添加笔记</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              if (!loading) {
                onClose?.();
              }
            }}
            disabled={loading}
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.tabHeader}>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'dbAi' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('dbAi')}
            >
              AI 笔记
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'realtimeAi' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('realtimeAi')}
            >
              实时 AI 笔记
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'manual' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('manual')}
            >
              手写笔记
            </button>
          </div>

          {activeTab === 'dbAi' ? (
            <div className={styles.tabContent}>
              <div className={aiNotesStyles.aiNotesWrapper}>
                {!dbAiNoteForSentence ? (
                  <div className={aiNotesStyles.notesStatus}>
                    <span>当前句子暂无已生成的 AI 笔记</span>
                  </div>
                ) : (
                  <div className={aiNotesStyles.aiNotesContent}>
                    <DbAiNoteCard
                      note={dbAiNoteForSentence}
                      displayId={dbAiNoteForSentence.sentence_id ?? sentenceIndex}
                      onWordClick={onWordClick}
                      aiNotesStyles={aiNotesStyles}
                      onSaveAiNote={onSaveAiNote}
                      isSaved={
                        (aiNoteSavedSet && aiNoteSavedSet.has(Number(sentenceIndex))) ||
                        Boolean(aiNoteSaved[String(sentenceIndex)] ?? aiNoteSaved[Number(sentenceIndex)])
                      }
                      isSaving={Boolean(aiNoteSaving[String(sentenceIndex)] ?? aiNoteSaving[Number(sentenceIndex)])}
                      savedNoteId={aiNoteSaved[String(sentenceIndex)] ?? aiNoteSaved[Number(sentenceIndex)] ?? null}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'realtimeAi' ? (
            <div className={styles.tabContent}>
              <div className={styles.aiSection}>
                <div className={styles.aiHeader}>
                  <h4>
                    <span className="material-icons-round" style={{ color: 'rgb(var(--theme-50))', fontSize: '20px', verticalAlign: 'middle', marginRight: '6px' }}>
                      auto_awesome
                    </span>
                    AI助手
                  </h4>
                  <div className={styles.aiActions}>
                    {aiStreaming && aiStreamingTab === activeAiTab && (
                      <span className={styles.aiStatus}>生成中...</span>
                    )}
                    <button
                      type="button"
                      className={styles.aiStopButton}
                      onClick={() => stopStreaming(true)}
                      disabled={!aiStreaming}
                    >
                      停止生成
                    </button>
                  </div>
                </div>

                <div className={styles.aiSubTabs}>
                  {tabConfigs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`${styles.aiSubTabButton} ${activeAiTab === tab.key ? styles.aiSubTabButtonActive : ''}`}
                      onClick={() => handleAiTabSelect(tab.key)}
                      disabled={aiStreaming && aiStreamingTab !== tab.key}
                    >
                      {tab.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={styles.aiRefreshButton}
                    onClick={() => fetchAiContent(activeAiTab, true)}
                    disabled={aiStreaming}
                  >
                  <span className="material-icons-round" style={{ fontSize: '16px', marginRight: '6px', verticalAlign: 'middle' }}>
                    refresh
                  </span>
                  重新生成
                  </button>
                </div>

                <div className={styles.aiContentBox}>
                  {aiStreaming && aiStreamingTab === activeAiTab && (
                    <div className={styles.aiGenerating}>AI 正在生成中...</div>
                  )}
                  {currentAiResponse ? (
                    <div className={styles.aiContent}>{formatWithBold(currentAiResponse)}</div>
                  ) : (
                    !aiStreaming && (
                      <div className={styles.aiPlaceholder}>点击上方标签或重新生成按钮获取AI笔记</div>
                    )
                  )}
                </div>

                {aiError && <div className={styles.aiError}>{aiError}</div>}

                <div className={styles.aiMessageActions}>
                  <button
                    type="button"
                    className={`${styles.aiSaveButton} ${currentAiSaveStatus === 'success' ? styles.aiSaveButtonActive : ''}`}
                    onClick={() => handleSaveAiResponse(activeAiTab)}
                    disabled={aiStreaming || !currentAiResponse.trim()}
                    title={currentAiSaveStatus === 'success' ? '已收藏' : '收藏AI笔记'}
                    aria-label={currentAiSaveStatus === 'success' ? '已收藏' : '收藏AI笔记'}
                  >
                    <HeartIcon
                      size={16}
                      filled={currentAiSaveStatus === 'success'}
                    />
                    <span className={styles.aiSaveButtonLabel}>
                      {currentAiSaveStatus === 'success' ? '已收藏' : '收藏笔记'}
                    </span>
                  </button>
                  {currentAiSaveStatus === 'error' && (
                    <span className={`${styles.aiSaveStatus} ${styles.aiSaveStatusError}`}>保存失败</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.tabContent}>
              <div className={styles.noteSection}>
                <label className={styles.inputLabel} htmlFor="note-content">
                  笔记内容
                </label>
                <textarea
                  id="note-content"
                  className={styles.textarea}
                  placeholder="写下你对这段内容的想法或标记..."
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  rows={6}
                  disabled={loading}
                  autoFocus
                />
                {error && <div className={styles.error}>{error}</div>}
              </div>
            </div>
          )}
        </div>

        {activeTab === 'manual' && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.submitButton}
              onClick={onSubmit}
              disabled={loading || !value.trim()}
            >
              {loading ? '保存中...' : '保存笔记'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default NoteModal;
